import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { PageHeader } from "@/components/primitives";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, Sprout, LayoutGrid, Flower2, Mail, ShieldCheck, KeyRound, Loader2, Copy } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (user?.is_admin) {
      api.get("/admin/users")
        .then((r) => setUsers(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [user]);

  if (user === null) return null;
  if (!user || !user.is_admin) return <Navigate to="/admin/login" replace />;

  const filtered = users.filter((u) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [u.name, u.email, u.garden_name, u.location]
      .filter(Boolean).some((x) => x.toLowerCase().includes(s));
  });

  return (
    <div data-testid="admin-users-page">
      <div className="pt-6">
        <Link to="/admin" className="chip" data-testid="admin-users-back">
          <ArrowLeft size={14} /> Admin
        </Link>
      </div>

      <PageHeader
        kicker="Admin · Gardeners"
        title="Everyone in the patch"
        subtitle={`${users.length} ${users.length === 1 ? "gardener" : "gardeners"} signed up`}
      />

      <input
        className="field mb-4"
        placeholder="Search by name, email, garden, location..."
        value={q}
        onChange={(e) => setQ(e.target.value)}
        data-testid="admin-users-search"
      />

      {loading ? (
        <p className="text-center font-display italic text-ink-soft py-12">Counting the gardeners...</p>
      ) : filtered.length === 0 ? (
        <p className="text-center font-display italic text-ink-soft py-12" data-testid="admin-users-empty">
          {q ? "No matches." : "No gardeners yet."}
        </p>
      ) : (
        <div className="space-y-2" data-testid="admin-users-list">
          {filtered.map((u) => (
            <article key={u.id} className="h-card p-4" data-testid={`admin-user-${u.id}`}>
              <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="font-display text-xl text-sage-dark flex items-center gap-2">
                    {u.name || "Anonymous"}
                    {u.is_admin_email && (
                      <span className="text-[10px] uppercase tracking-wider bg-terracotta text-cream-soft px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <ShieldCheck size={10} /> Admin
                      </span>
                    )}
                  </div>
                  <a href={`mailto:${u.email}`} className="text-sm text-ink-soft hover:underline truncate inline-block max-w-full">
                    {u.email}
                  </a>
                  {u.garden_name && <div className="font-display italic text-ink-soft text-sm">{u.garden_name}{u.location ? ` · ${u.location}` : ""}</div>}
                </div>
                <div className="text-xs text-ink-soft whitespace-nowrap">
                  Joined {fmt(u.created_at)}
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <Stat icon={Sprout} value={u.plants_count} label="plants" />
                <Stat icon={LayoutGrid} value={u.beds_count} label="beds" />
                <Stat icon={Flower2} value={u.blooms_count} label="blooms" />
                <a href={`mailto:${u.email}`} className="chip" data-testid={`admin-user-mail-${u.id}`}>
                  <Mail size={12} /> Write
                </a>
                <ResetLinkButton user={u} />
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function ResetLinkButton({ user }) {
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/users/${user.id}/reset-link`);
      const url = r.data.reset_link;
      setLink(url);
      // Try clipboard
      try { await navigator.clipboard.writeText(url); toast.success("Reset link copied — valid 1 hour"); }
      catch { toast.success("Reset link generated"); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't create a reset link");
    } finally { setBusy(false); }
  };

  const copy = async () => {
    if (!link) return;
    try { await navigator.clipboard.writeText(link); toast.success("Copied"); }
    catch { /* user can select manually */ }
  };

  if (link) {
    return (
      <div className="basis-full sm:basis-auto sm:max-w-full flex items-center gap-2 mt-1" data-testid={`admin-user-reset-link-${user.id}`}>
        <input readOnly value={link} className="field text-xs flex-1 min-w-0" onFocus={(e) => e.target.select()} />
        <button type="button" className="chip" onClick={copy} data-testid={`admin-user-copy-${user.id}`}><Copy size={12} /> Copy</button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={generate}
      disabled={busy}
      className="chip hover:bg-cream-deep"
      title="Generate a one-hour password reset link"
      data-testid={`admin-user-reset-${user.id}`}
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />}
      {busy ? "Creating…" : "Reset password"}
    </button>
  );
}

function Stat({ icon: Icon, value, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 chip py-1 px-2.5">
      <Icon size={12} className="text-sage-dark" />
      <span className="tabular-nums">{value}</span>
      <span className="text-ink-soft">{label}</span>
    </span>
  );
}

function fmt(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-NZ", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return s; }
}
